import React, { FC, useMemo } from "react";
import { Button } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { formatISO, startOfYear, toDate } from "date-fns";
import { get } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { onScrollBottomIndicator } from "~/hooks/selectIndicatorValue";
import { useGetAllDealersForSelect } from "~/hooks/Selectors/useGetAllDealersForSelect";
import {
  type OptionDownloadConfig,
  SELECT_FILE_TYPE_OPTIONS,
  SELECT_TYPE_OPTIONS,
} from "~/pages/Uploads/modals/downloadOptions";
import {
  useDownloadDealersValueLazyQuery,
  useDownloadIndicatorsValueLazyQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useNotify } from "~/shared/hooks/useNotify";
import { processArray } from "~/shared/lib/processArray";
import { Field } from "~/shared/ui/Field";

const toJsDate = (value) =>
  value ? (value instanceof Date ? value : value.toDate()) : value;

const DownloadForm: FC = () => {
  const { t } = useTranslation();

  const { hasPermission, loading: loadingPermissions } = usePermissions();

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      dateBegin: startOfYear(new Date()),
      dateEnd: toDate(new Date()),
    },
  });

  const {
    onScrollBottomIndicators,
    indicatorsForSelect,
    searchIndicator,
    loading,
  } = onScrollBottomIndicator();
  const isTypeIndicators = watch("downloadType") === "download_indicators";
  const isNotSelectedYear = !watch("year");

  const filteredOptions = useMemo(() => {
    if (loadingPermissions) {
      return [];
    }
    return processArray(hasPermission, SELECT_TYPE_OPTIONS).map(
      (option) => (option as OptionDownloadConfig).value,
    );
  }, [loadingPermissions, hasPermission]);

  const downloadTypeOptions = useMemo(
    () =>
      filteredOptions.map((value) => ({
        value,
        label: t(`downloads.${value}`),
      })),
    [filteredOptions, t],
  );

  const selectYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = [{ value: "", label: t("common.not_selected") }];
    for (let i = currentYear; i > currentYear - 20; i--) {
      years.push({ value: String(i), label: String(i) });
    }
    return years;
  }, [t]);

  const fileTypeOptions = useMemo(
    () =>
      SELECT_FILE_TYPE_OPTIONS.map((value) => ({
        value,
        label: t(`downloads.${value}`),
      })),
    [t],
  );

  const { notifyReport } = useNotify();
  const [downloadIndicatorsValueLazyQuery, indicatorsValueLazyQuery] =
    useDownloadIndicatorsValueLazyQuery({
      onCompleted(data) {
        const hash = get(
          data,
          "IndicatorGetter.downloadIndicatorsValue.fileHash",
        );
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  const [doownloadDealersValueLazyQuery, dealersValueLazyQuery] =
    useDownloadDealersValueLazyQuery({
      onCompleted(data) {
        const hash = get(data, "DealerGetter.downloadDealers.fileHash");
        hash && notifyReport(hash);
      },
      fetchPolicy: "no-cache",
    });

  const {
    dealers: dealershipsForSelect,
    onScrollBottomDealers,
    searchDealer,
    loading: loadingDealers,
  } = useGetAllDealersForSelect({ searchDebounceMs: 400 });

  const handlePopupScroll = (onScrollBottom?: () => void) => (e) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
      onScrollBottom?.();
    }
  };

  const onSubmit = ({
    DealerIds,
    IndicatorIds,
    year,
    dateBegin,
    dateEnd,
    type,
  }) => {
    const formattedDateBegin = dateBegin
      ? formatISO(toJsDate(dateBegin))
      : dateBegin;
    const formattedDateEnd = dateEnd ? formatISO(toJsDate(dateEnd)) : dateEnd;

    isTypeIndicators
      ? downloadIndicatorsValueLazyQuery({
          variables: {
            DealerIds,
            IndicatorIds,
            ...(() =>
              year
                ? { year: Number(year) }
                : {
                    dateBegin: formattedDateBegin,
                    dateEnd: formattedDateEnd,
                  })(),
          },
        })
      : doownloadDealersValueLazyQuery({
          variables: {
            DealerIds,
            type,
          },
        });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex vertical gap={16}>
        <Field
          type="select"
          name="downloadType"
          control={control}
          label={t("downloads.downloadType")}
          options={downloadTypeOptions}
        />
        {isTypeIndicators ? (
          <>
            <Field
              type="select"
              name="year"
              control={control}
              label={t("downloads.year")}
              options={selectYearOptions}
            />
            {isNotSelectedYear && (
              <Flex gap={10}>
                <Field
                  type="datePicker"
                  name="dateBegin"
                  control={control}
                  label={t("ui.from")}
                />
                <Field
                  type="datePicker"
                  name="dateEnd"
                  control={control}
                  label={t("ui.to")}
                />
              </Flex>
            )}
          </>
        ) : (
          <Field
            type="select"
            name="type"
            control={control}
            label={t("downloads.type")}
            options={fileTypeOptions}
          />
        )}
        <Field
          type="select"
          name="DealerIds"
          control={control}
          label={t("downloads.dealerships_choice")}
          mode="multiple"
          options={dealershipsForSelect}
          showSearch
          filterOption={false}
          onSearch={searchDealer}
          onPopupScroll={handlePopupScroll(onScrollBottomDealers)}
          loading={loadingDealers}
        />
        {isTypeIndicators && (
          <Field
            type="select"
            name="IndicatorIds"
            control={control}
            label={t("downloads.indicators_choice")}
            mode="multiple"
            options={indicatorsForSelect}
            showSearch
            filterOption={false}
            onSearch={searchIndicator}
            onPopupScroll={handlePopupScroll(onScrollBottomIndicators)}
            loading={loading}
          />
        )}
        <Flex justify="flex-end" gap={10}>
          <Button
            fontSize={"14"}
            htmlType="submit"
            color="primary"
            loading={
              indicatorsValueLazyQuery.loading || dealersValueLazyQuery.loading
            }
          >
            {t("ui.download_file")}
          </Button>
        </Flex>
      </Flex>
    </form>
  );
};
export default DownloadForm;
